import React, { useState, useCallback } from 'react';
import { FormField, Input, Select } from '../ui/Form';
import { Calendar, RotateCcw } from 'lucide-react';
import { ACCOUNT_TYPES, ACCOUNT_TYPE_LABELS } from '../../config/accounting';

interface LedgerFiltersProps {
  onFilterChange: (filters: LedgerFilters) => void;
  /** Valeurs initiales des filtres */
  initialFilters?: Partial<LedgerFilters>;
}

export interface LedgerFilters {
  search: string;
  accountType: string;
  startDate: string;
  endDate: string;
  balanceType: 'all' | 'debit' | 'credit';
}

const defaultFilters: LedgerFilters = {
  search: '',
  accountType: 'all',
  startDate: '',
  endDate: '',
  balanceType: 'all'
};

/**
 * Filtres métier pour le grand livre
 * Note: La recherche textuelle est gérée par DataTable (enableGlobalFilter)
 * Ce composant se concentre sur les filtres métier spécifiques
 */
export function LedgerFilters({ onFilterChange, initialFilters }: LedgerFiltersProps) {
  // État local des filtres pour persistance
  const [filters, setFilters] = useState<LedgerFilters>({
    ...defaultFilters,
    ...initialFilters
  });

  // Mise à jour d'un filtre en préservant les autres
  const handleChange = useCallback((field: keyof LedgerFilters, value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  }, [filters, onFilterChange]);

  // Réinitialiser tous les filtres
  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
    onFilterChange(defaultFilters);
  }, [onFilterChange]);

  // Vérifier si des filtres sont actifs (sans le search car géré par DataTable)
  const hasActiveFilters = 
    filters.accountType !== 'all' ||
    filters.startDate !== '' ||
    filters.endDate !== '' ||
    filters.balanceType !== 'all';

  return (
    <div className="space-y-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField label="Type de compte">
          <Select
            value={filters.accountType}
            options={[
              { value: 'all', label: 'Tous les types' },
              ...Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => ({
                value,
                label
              }))
            ]}
            onChange={(e) => handleChange('accountType', e.target.value)}
          />
        </FormField>

        <FormField label="Date début">
          <Input
            type="date"
            icon={Calendar}
            value={filters.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
          />
        </FormField>

        <FormField label="Date fin">
          <Input
            type="date"
            icon={Calendar}
            value={filters.endDate}
            onChange={(e) => handleChange('endDate', e.target.value)}
          />
        </FormField>

        <FormField label="Type de solde">
          <Select
            value={filters.balanceType}
            options={[
              { value: 'all', label: 'Tous les soldes' },
              { value: 'debit', label: 'Solde débiteur' },
              { value: 'credit', label: 'Solde créditeur' }
            ]}
            onChange={(e) => handleChange('balanceType', e.target.value as LedgerFilters['balanceType'])}
          />
        </FormField>
      </div>

      {/* Bouton réinitialiser si filtres actifs */}
      {hasActiveFilters && (
        <div className="flex justify-end">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Réinitialiser les filtres
          </button>
        </div>
      )}
    </div>
  );
}